import { CommonModule } from '@angular/common';
import { Component, ElementRef, Renderer2, signal, ViewChild, computed, effect } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronLeft, faFloppyDisk, faCircle, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { faClockRotateLeft } from '@fortawesome/pro-light-svg-icons';
import { ActivitiesFormComponent } from '../../../components/forms/activities-form/activities-form.component';
import { AddressesFormComponent } from '../../../components/forms/addresses-form/addresses-form.component';
import { ContactsFormComponent } from '../../../components/forms/contacts-form/contacts-form.component';
import { DeclarationsFormComponent } from '../../../components/forms/declarations-form/declarations-form.component';
import { IdentificationFormComponent } from '../../../components/forms/identification-form/identification-form.component';
import { InvoicingFormComponent } from '../../../components/forms/invoicing-form/invoicing-form.component';
import { LaborDataFormComponent } from '../../../components/forms/labor-data-form/labor-data-form.component';
import { OtherDataFormComponent } from '../../../components/forms/other-data-form/other-data-form.component';
import { FiscalDataFormComponent } from '../../../components/forms/fiscal-data-form/fiscal-data-form.component';
import { SocietyDataFormComponent } from '../../../components/forms/society-data-form/society-data-form.component';
import { WorkTeamFormComponent } from '../../../components/forms/work-team-form/work-team-form.component';
import { ConstitutionFormComponent } from '../../../components/forms/constitution-form/constitution-form.component';
import { BooksFormComponent } from '../../../components/forms/books-form/books-form.component';
import { OperacionesSocietariasFormComponent } from '../../../components/forms/operaciones-societarias-form/operaciones-societarias-form.component';
import { HistoricoDrawerComponent } from '../../../components/historico-drawer/historico-drawer.component';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CLIENT_RELATION_TYPES, CLIENT_TYPES } from '../../../core/constants/client.constants';
import { faArrowAltCircleDown, faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { ClientTabs } from '../../../core/interfaces/client-tabs';
import { PermissionService } from '../../../core/services/permission.service';
import { Permission } from '../../../core/enums/permission.enum';
import { resolveClientDetailBackUrl, parseClientDetailBackNavigation } from '../../../core/utils/client-navigation.util';
import { AuditService } from '../../../core/services/audit/audit.service';
import { ClientSnapshot } from '../../../core/models/audit.model';

@Component({
  selector: 'app-client-detail',
  standalone: true,
  imports: [RouterModule, CommonModule, FontAwesomeModule, ActivitiesFormComponent, AddressesFormComponent, ContactsFormComponent, DeclarationsFormComponent, IdentificationFormComponent, InvoicingFormComponent, LaborDataFormComponent, OtherDataFormComponent, SocietyDataFormComponent, ProgressSpinnerModule, FiscalDataFormComponent, WorkTeamFormComponent, BooksFormComponent, ConstitutionFormComponent, OperacionesSocietariasFormComponent, HistoricoDrawerComponent],
  templateUrl: './client-detail.component.html',
  styleUrl: './client-detail.component.scss'
})
export class ClientDetailComponent {
  @ViewChild('activeForm') activeForm: Partial<{ save: Function }>;
  @ViewChild('progressBar') progressBar: ElementRef;

  faChevronLeft = faChevronLeft;
  faArrowRight = faArrowRight;
  faFloppyDisk = faFloppyDisk;
  faCircle = faCircle;
  faTriangleExclamation = faTriangleExclamation;
  faClockRotateLeft = faClockRotateLeft;

  drawerOpen = signal(false);
  snapshotDate = signal<Date | null>(null);
  snapshotData = signal<ClientSnapshot | null>(null);
  isSnapshotLoading = signal(false);

  toggleHistorico() { this.drawerOpen.update(v => !v); }
  clearSnapshot() {
    this.snapshotDate.set(null);
    this.snapshotData.set(null);
  }

  isSaved = false;
  showButton = false;
  clientTypes = [
    { label: 'Cliente', value: 'cliente' },
    { label: 'Proveedor', value: 'proveedor' },
    { label: 'Cliente/Proveedor', value: 'cliente-proveedor' },
  ];

  tabs = signal<ClientTabs[]>([]);

  clientId: any;
  client: any;
  clientType = undefined;
  personTypes = CLIENT_TYPES;
  relationTypes = CLIENT_RELATION_TYPES;
  relationType = signal<any>(undefined);
  relationMenuOpen = signal<boolean>(false);
  clientState = signal<string | undefined>(undefined);

  enabledTabs = true;
  isLoading: boolean = false;
  hasEditPermission: boolean = false;

  // list state preserved via query params
  private listTab: string | undefined;
  private listPage: number = 0;
  private listClientId: number | undefined;
  private listClientCode: string | undefined;
  private listPersonType: string | undefined;
  private listResponsible: string | undefined;
  private returnUrl: string | undefined;
  private returnClientTab: string | undefined;
  private initialActiveTab: string | undefined;
  private itemsForPage: number = 10;

  constructor(
    private route: ActivatedRoute,
    private clientsService: ClientsService,
    private renderer: Renderer2,
    private router: Router,
    private permissionService: PermissionService,
    private auditService: AuditService,
  ) {
    effect(async () => {
      const date = this.snapshotDate();
      if (!date || !this.clientId) {
        this.snapshotData.set(null);
        return;
      }
      this.isSnapshotLoading.set(true);
      try {
        const snapshot = await this.auditService.getClientSnapshot(this.clientId, date);
        this.snapshotData.set(snapshot);
      } catch {
        this.snapshotData.set(null);
      } finally {
        this.isSnapshotLoading.set(false);
      }
    }, { allowSignalWrites: true });
    this.route.paramMap.subscribe(async params => {
      this.isLoading = true;
      // Otro cliente: no arrastrar el modo histórico del anterior.
      this.snapshotDate.set(null);
      this.snapshotData.set(null);
      this.hasEditPermission = this.permissionService.hasPermission(Permission.ManageClients);
      const qp = this.route.snapshot.queryParams;
      this.listTab = qp['tab'] || undefined;
      this.listPage = qp['page'] ? Number(qp['page']) : 0;
      this.listClientId = qp['clientId'] ? Number(qp['clientId']) : undefined;
      this.listClientCode = qp['clientCode'] || undefined;
      this.listPersonType = qp['personType'] || undefined;
      this.listResponsible = qp['responsible'] || undefined;
      this.returnUrl = qp['returnUrl'] || undefined;
      this.returnClientTab = qp['returnClientTab'] || undefined;
      this.initialActiveTab = qp['clientTab'] || undefined;
      const id = params.get('id');
      if (id === 'new') {
        const personType = this.route.snapshot.queryParamMap.get('personType');
        this.clientType = this.personTypes.find(p => p.value === personType)
        const relationType = this.route.snapshot.queryParamMap.get('relationType');
        this.relationType.set(this.relationTypes.find(r => r.value === relationType));
        this.clientState.set('PENDING');

        this.enabledTabs = false;
        this.updateTabs();

      } else {
        this.clientId = Number(id);
        try {
          await this.getClient();
          this.enabledTabs = true;
          this.updateTabs();
          // Check certificate after tabs are initialized
          await this.checkDigitalCertificateStatus();
        } finally {
          this.isLoading = false; // un 404/500 no debe dejar el spinner para siempre
        }
      }

      this.isLoading = false;
    });
  }

  /** showLoading=false refresca los datos sin desmontar el formulario activo (mensaje de guardado y ediciones). */
  async getClient(showLoading = true) {
    if (showLoading) this.isLoading = true;
    try {
      this.client = await this.clientsService.getClientById(this.clientId);
      this.clientType = this.personTypes.find(p => p.value === this.client.personType)
      this.relationType.set(this.relationTypes.find(r => r.value === this.client.relationType));
      this.clientState.set(this.client.state);
    } finally {
      if (showLoading) this.isLoading = false;
    }
  }


  async updateClientState(newState: string) {
    this.clientState.set(newState);
    if (this.client) {
      this.client.state = newState;
    }
    // Refresh client data to get updated dates (endDate, reactivationDate)
    await this.getClient(false);
  }

  updateTabs() {
    const relationValue = this.relationType()?.value;
    const isNoClient = relationValue === 'NO_CLIENT';
    const isRecurrent = relationValue === 'RECURRENT';
    const isJuridicPerson = this.clientType?.value === 'JURIDIC_PERSON';


    const tabConfigs = [
      { title: 'Identificación', key: 'identification', show: true },
      { title: 'Operaciones societarias', key: 'operacionesSocietarias', show: isJuridicPerson && isRecurrent },
      { title: 'Constitución', key: 'constitution', show: false }, // replaced by Operaciones societarias
      { title: 'Domicilios', key: 'addresses', show: true },
      { title: 'Actividades', key: 'activities', show: isRecurrent },
      { title: 'Laboral', key: 'laborData', show: isRecurrent },
      { title: 'Contactos y usuarios', key: 'contacts', show: true },
      { title: 'Otros datos fiscales', key: 'fiscalData', show: isRecurrent },
      { title: 'Libros', key: 'books', show: isRecurrent },
      { title: 'Interno', key: 'workTeam', show: isRecurrent },
      { title: 'Cuentas bancarias', key: 'otherData', show: !isNoClient },
      { title: 'Declaraciones', key: 'declarations', show: !isNoClient },
    ];

    const visibleTabs = tabConfigs.filter(t => t.show);
    const existingTabs = this.tabs();
    let preferredTabKey = this.initialActiveTab ?? this.getActiveTab()?.key ?? 'identification';

    if (!visibleTabs.find(t => t.key === preferredTabKey)) {
      preferredTabKey = 'identification';
    }

    const newTabs: ClientTabs[] = visibleTabs.map(t => {
      const existingTab = existingTabs.find(et => et.key === t.key);
      return {
        title: t.title,
        key: t.key,
        isActive: t.key === preferredTabKey,
        hasAlert: existingTab?.hasAlert || false
      };
    });

    this.tabs.set(newTabs);
    this.initialActiveTab = undefined;
  }

  setRelationType(typeValue: string) {
    const newType = this.relationTypes.find(r => r.value === typeValue);
    if (newType) {
      this.relationType.set(newType);
      this.updateTabs();

      if (this.client) {
        this.client.relationType = newType.value;
      }
    }
  }

  toggleRelationMenu() {
    this.relationMenuOpen.update(open => !open);
  }

  closeRelationMenu() {
    this.relationMenuOpen.set(false);
  }

  selectRelationType(typeValue: string) {
    this.setRelationType(typeValue);
    this.closeRelationMenu();
  }


  async changeTab(tab: any) {
    this.tabs.update(tabs => tabs.map((t: any) => {
      t.isActive = t.title === tab.title;
      return t;
    }));
    this.showButton = false;

    // Si la pestaña activa es "declarations", forzar refresh del componente hijo
    if (tab.key === 'declarations' && this.activeForm && 'refreshObservationsIndicators' in this.activeForm) {
      // Pequeño delay para asegurar que el componente está visible
      setTimeout(async () => {
        await (this.activeForm as any).refreshObservationsIndicators();
      }, 100);
    }
  }

  onCertificateExpired(isExpired: boolean) {
    this.tabs.update(tabs => tabs.map(t => {
      if (t.key === 'fiscalData') {
        return { ...t, hasAlert: isExpired };
      }
      return t;
    }));
  }

  async checkDigitalCertificateStatus() {
    // Sin certificados (o si falla la carga) no debe quedar el aviso del cliente anterior.
    this.tabs.update(tabs => tabs.map(t => (t.key === 'fiscalData' ? { ...t, hasAlert: false } : t)));
    try {
      const fiscalData = await this.clientsService.getClientFiscalData(this.clientId);
      if (fiscalData && fiscalData.digitalCertificates && fiscalData.digitalCertificates.length > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Check if any certificate is expired
        const isExpired = fiscalData.digitalCertificates.some((cert: any) => {
          if (cert.expiryDate) {
            const expiry = new Date(cert.expiryDate);
            expiry.setHours(0, 0, 0, 0);
            return expiry < today;
          }
          return false;
        });

        this.tabs.update(tabs => tabs.map(t => {
          if (t.key === 'fiscalData') {
            return { ...t, hasAlert: isExpired };
          }
          return t;
        }));
      }
    } catch (error) {
      // If fiscal data doesn't exist or there's an error, just continue
    }
  }

  getActiveTab() {
    return this.tabs().find(tab => tab.isActive);
  }

  async saveTab() {
    await this.activeForm.save().then(() => {
      this.isSaved = true;
      let width = 0;
      this.showButton = false;
      const interval = setInterval(() => {
        if (width >= 100) {
          clearInterval(interval);
          this.isSaved = false;
        } else {
          width++;
          this.renderer.setProperty(this.progressBar.nativeElement, 'value', width);
        }
      }, 30);
    }).catch((e) => {
    });
  }

  async onClientCreated(event) {
    this.clientId = event;
    await this.getClient();
    this.enabledTabs = true;
    this.updateTabs();

    this.router.navigate(['/clients/detail', this.clientId], { replaceUrl: true });
  }

  goBack() {
    if (this.returnUrl) {
      const backUrl = resolveClientDetailBackUrl(this.returnUrl, this.returnClientTab);
      const parsed = parseClientDetailBackNavigation(backUrl);
      if (parsed) {
        this.router.navigate(['/clients/detail', parsed.clientId], {
          queryParams: parsed.clientTab ? { clientTab: parsed.clientTab } : {},
        });
        return;
      }
      this.router.navigateByUrl(backUrl);
      return;
    }
    const queryParams: any = {
      tab: this.listTab || null,
      page: this.listPage ?? 0,
      clientId: this.listClientId ?? null,
      clientCode: this.listClientCode ?? null,
      personType: this.listPersonType ?? null,
      responsible: this.listResponsible ?? null
    };
    this.router.navigate(['/clients'], { queryParams });
  }

  private isNavigatingSibling = false;

  async goToNext() {
    await this.navigateToSibling(true);
  }

  private async navigateToSibling(isNext: boolean) {
    if (this.isNavigatingSibling) return; // doble clic = dos peticiones y dos navegaciones
    this.isNavigatingSibling = true;
    try {
      await this.findAndNavigateToSibling(isNext);
    } finally {
      this.isNavigatingSibling = false;
    }
  }

  private async findAndNavigateToSibling(isNext: boolean) {
    const pageData = await this.clientsService.getClients(
      this.listPage || 0,
      this.itemsForPage,
      this.listClientId,
      this.listClientCode,
      this.listPersonType,
      this.listResponsible,
      this.listTab === 'pending' ? 'PENDING' : undefined
    );
    const clients: any[] = pageData.clients || [];
    const currentIndex = clients.findIndex(c => c.id === this.clientId);

    if (currentIndex !== -1) {
      const targetIndex = isNext ? currentIndex + 1 : currentIndex - 1;
      if (targetIndex >= 0 && targetIndex < clients.length) {
        this.navigateWithQuery(clients[targetIndex].id);
        return;
      }
    }

    // Cliente fuera de la página listada (enlace sin contexto, recién activado...): no hay un "siguiente"
    // fiable, y saltar a la página contigua llevaría a un cliente que no es el siguiente.
    if (currentIndex === -1) return;

    const totalPages = (pageData.totalPages ?? 0) + 1;
    const nextPage = isNext ? this.listPage + 1 : this.listPage - 1;
    if (nextPage >= 0 && nextPage < totalPages) {
      const adjacentPageData = await this.clientsService.getClients(
        nextPage,
        this.itemsForPage,
        this.listClientId,
        this.listClientCode,
        this.listPersonType,
        this.listResponsible,
        this.listTab === 'pending' ? 'PENDING' : undefined
      );
      const adjacentClients: any[] = adjacentPageData.clients || [];
      if (adjacentClients.length > 0) {
        const target = isNext ? adjacentClients[0] : adjacentClients[adjacentClients.length - 1];
        this.listPage = nextPage;
        this.navigateWithQuery(target.id);
        return;
      }
    }
  }

  private navigateWithQuery(id: number) {
    const queryParams: any = {
      tab: this.listTab || null,
      page: this.listPage ?? 0,
      clientId: this.listClientId ?? null,
      clientCode: this.listClientCode ?? null,
      personType: this.listPersonType ?? null,
      responsible: this.listResponsible ?? null
    };
    this.router.navigate(['/clients/detail', id], { queryParams });
  }

}
